import { apiRequest } from "../client";
import type {
  Dashboard,
  DashboardWidget,
  DashboardResponse,
  DashboardListResponse,
  DashboardWidgetResponse,
  CreateDashboardRequest,
  CreateWidgetRequest,
  DashboardPosition,
  DashboardChatResponse,
  AniInsightResponse,
  HotActionsResponse,
  TickerTapeResponse,
} from "./types";

const BASE_URL = "/dashboards";

/**
 * Get all dashboards
 */
export const list = async (): Promise<Dashboard[]> => {
  const response = await apiRequest<Dashboard[]>({
    method: "GET",
    url: `${BASE_URL}/`,
  });
  return response;
};

/**
 * Get a specific dashboard by ID
 */
export const get = async (dashboardId: string): Promise<DashboardResponse> => {
  return apiRequest<DashboardResponse>({
    method: "GET",
    url: `${BASE_URL}/${dashboardId}/`,
  });
};

/**
 * Create a new dashboard
 */
export const create = async (
  data: CreateDashboardRequest
): Promise<DashboardResponse> => {
  return apiRequest<DashboardResponse>({
    method: "POST",
    url: `${BASE_URL}/`,
    data,
  });
};

/**
 * Remove a dashboard
 */
export const remove = async (
  dashboardId: string
): Promise<DashboardResponse> => {
  return apiRequest<DashboardResponse>({
    method: "DELETE",
    url: `${BASE_URL}/${dashboardId}/`,
  });
};

/**
 * Add a widget to a dashboard
 */
export const addWidget = async (
  dashboardId: string,
  data: CreateWidgetRequest
): Promise<DashboardWidgetResponse> => {
  console.log("data", data);
  return apiRequest<DashboardWidgetResponse>({
    method: "POST",
    url: `${BASE_URL}/${dashboardId}/widgets/`,
    data,
  });
};

/**
 * Remove a widget from a dashboard
 */
export const removeWidget = async (
  dashboardId: string,
  widgetId: string
): Promise<void> => {
  return apiRequest<void>({
    method: "DELETE",
    url: `${BASE_URL}/${dashboardId}/widgets/${widgetId}/`,
  });
};

/**
 * Update the position of a widget on a dashboard
 */
export const updateWidgetPosition = async (
  dashboardId: string,
  widgetId: string,
  position: DashboardPosition
): Promise<void> => {
  return apiRequest<void>({
    method: "PUT",
    url: `${BASE_URL}/${dashboardId}/widgets/${widgetId}/`,
    data: {
      position,
    },
  });
};

/**
 * Send a chat message in the dashboard context
 */
export const sendDashboardChatMessage = async (
  dashboardId: string,
  question: string,
  history: { role: string; content: string }[] = []
): Promise<DashboardChatResponse> => {
  return apiRequest<DashboardChatResponse>({
    method: "POST",
    url: `/ani-experts/dashboards/chat`,
    // url: `/ani-experts/dashboards/chat?dashboard_id=${dashboardId}`,
    data: {
      question,
      history,
    },
  });
};

export const getAniInsight = async (): Promise<AniInsightResponse> => {
  return apiRequest<AniInsightResponse>({
    method: "GET",
    url: `${BASE_URL}/ani-insight/`,
  });
};

export const getHotActions = async (): Promise<HotActionsResponse> => {
  return apiRequest<HotActionsResponse>({
    method: "GET",
    url: `${BASE_URL}/hot-actions/`,
  });
};

export const getTickerTapeData = async (): Promise<TickerTapeResponse> => {
  return apiRequest<TickerTapeResponse>({
    method: "GET",
    url: `${BASE_URL}/ticker-tape/`,
  });
};

/**
 * Update a dashboard's properties
 */
export const update = async (
  dashboardId: string,
  data: Partial<CreateDashboardRequest> & { icon?: string; color?: string }
): Promise<DashboardResponse> => {
  return apiRequest<DashboardResponse>({
    method: "PUT",
    url: `${BASE_URL}/${dashboardId}/`,
    data,
  });
};
